import React, {useEffect} from "react";
import {Button, Grid, Typography} from "@mui/material";
import {Edit} from "@mui/icons-material";
import {cleanCondition, parseCondition} from "../../../../inventory/shared/mapping_templates";
import EnhancedMappingEditor from "./EnhancedMappingEditor";
import { pageContentGap } from '../../../../../theme';

// region TypeScript Interfaces
interface AttributeOption {
    Name: string;
    FriendlyName: string;
    Type?: string;
    Options?: unknown[];
    [key: string]: unknown;
}

interface FilterTargetOption {
    label: string;
    value: string;
    type?: string;
    inputOptions?: unknown[];
}

interface FilterSourceOption {
    label: string;
    value: string;
    [key: string]: unknown;
}

interface FilterBlockProps {
    type: string;
    attributes: AttributeOption[];
    profile: { Filter?: unknown; [key: string]: unknown };
    setProfile: (updater: (prev: any) => any) => void;
    isRetailer?: boolean;
    noOptions?: boolean;
}
// endregion

export default function FilterBlock(props: FilterBlockProps) {
    const {type, attributes, profile, setProfile} = props;

    const [currentTargetOptions, setCurrentTargetOptions] = React.useState<FilterTargetOption[]>([]);
    const [currentSourceOptions, setCurrentSourceOptions] = React.useState<FilterSourceOption[]>([]);

    const [loading, setLoading] = React.useState(true);
    const [filterEditorOpen, setFilterEditorOpen] = React.useState(false);
    const [filterString, setFilterString] = React.useState("");

    useEffect(() => {
        const productsSetup = () => {
            setLoading(true);
            let filteredAttributes = attributes.filter(attribute => attribute.Type !== "image"
                && attribute.Type !== "imagemeta" && attribute.Type !== "kvplist" && attribute.Type !== "list");
            setCurrentTargetOptions(filteredAttributes.map(attribute => ({
                label: attribute.Name,
                value: attribute.Name,
                type: attribute.Type,
                inputOptions: attribute.Options || []
            })));
            setCurrentSourceOptions(attributes.map((attribute) => ({label: attribute.FriendlyName, value: attribute.Name})));
            setLoading(false);
        }

        const attributesSetup = () => {
            setLoading(true);
            let options = ["Name", "FriendlyName", "Type", "Description", "Options", "Group", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];
            setCurrentTargetOptions(options.map(option => ({
                label: option,
                value: option,
                type: option === "LastModified" ? "date" : "text",
                inputOptions: []
            })));
            setCurrentSourceOptions(options.map(option => ({label: option, value: option})));
            setLoading(false);
        }

        const attributeSetsSetup = () => {
            setLoading(true);
            let options = ["Name", "FriendlyName", "Attributes", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];
            setCurrentTargetOptions(options.map(option => ({
                label: option,
                value: option,
                type: option === "LastModified" ? "date" : "text",
                inputOptions: []
            })));
            setCurrentSourceOptions(options.map(option => ({label: option, value: option})));
            setLoading(false);
        }

        const categoriesSetup = () => {
            setLoading(true);
            let options = ["Id", "ParentId", "Name", "AttributeSet", "DestinationId", "BannerURL", "SyncStatus", "Level", "Children", "Path", "LastModified"];
            setCurrentTargetOptions(options.map(option => ({
                label: option,
                value: option,
                type: option === "LastModified" ? "date" : "text",
                inputOptions: []
            })));
            setCurrentSourceOptions(options.map(option => ({label: option, value: option})));
            setLoading(false);
        }

        const inventoryManagementSetup = () => {
            setLoading(true);
            let options = ["Quantity", "Cost", "MerchantSku", "SupplierSku", "UPC", "ManufacturerSKU", "MSRP", "RetailPrice",
                "SupplierDescription", "MerchantDescription", "Size", "Color", "Style", "Weight", "Warehouse", "NextAvailableQuantity",
                "NextAvailableDate", "UOM",
                "LastUpdate",
                "LeadTime",
                "Status",
                "Custom1", "Custom2", "Custom3", "Custom4", "Custom5",
                ...Array.from({length: 9}, (_, i) => [
                    `Warehouse${i + 2}`,
                    `Warehouse${i + 2}Quantity`,
                    `Warehouse${i + 2}NextAvailableQuantity`, 
                    `Warehouse${i + 2}NextAvailableDate`
                ]).flat()
            ];
            setCurrentTargetOptions(options.map(option => ({
                label: option,
                value: option,
                type: option === "LastUpdate" ? "date" : "text",
                inputOptions: []
            })));
            setCurrentSourceOptions(options.map(option => ({label: option, value: option})));
            setLoading(false);
        }

        switch (type) {
            case "products":
                productsSetup();
                break;
            case "supplierProducts":
                productsSetup();
                break;
            case "categories":
                categoriesSetup();
                break;
            case "attributes":
                attributesSetup();
                break;
            case "attributeSets":
                attributeSetsSetup();
                break;
            case "inventoryManagement":
                inventoryManagementSetup();
                break;
            default:
                productsSetup();
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributes]);

    useEffect(() => {
        let parsedFilterString = profile.Filter !== undefined
            ? parseCondition(profile.Filter, currentTargetOptions)
            : "";

        if (parsedFilterString)
            parsedFilterString = "Export enabled items where " + parsedFilterString + ".";
        else
            parsedFilterString = "Export all enabled items.";

        setFilterString(parsedFilterString);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.profile]);

    return (
        <Grid item container alignItems={"flex-start"} style={{gap: pageContentGap, flexDirection: "column"}}>
            <EnhancedMappingEditor
                options={currentSourceOptions}
                condition={profile.Filter}
                
                noOptions={props.noOptions}

                source={""}
                target={""}

                title={"Filter Editor"}
                open={filterEditorOpen}
                onClose={() => setFilterEditorOpen(false)}

                onChange={(values: any) => {
                    let cleanedCondition = cleanCondition(values.Condition);
                    setProfile(prevState => {
                        return ({...prevState, Filter: cleanedCondition});
                    })
                }}
            />

            <Typography>
                {filterString}
            </Typography>

            <Button variant="outlined" color="primary"
                    onClick={() => setFilterEditorOpen(true)}
                    disabled={loading}
            >
                <Edit style={{marginRight: "6px"}}/> Edit Filters
            </Button>
        </Grid>
    )
}
